/**
 * Botón global "Enviar comentario" — disponible para CUALQUIER usuario autenticado
 * (vive en el Topbar). Abre un modal con categoría (SUGERENCIA|PROBLEMA|OTRO) +
 * mensaje, y postea a POST /feedback. El backend toma tenant/usuario del token; la
 * lectura cross-tenant es solo del platform-admin. Confirmación inline al enviar.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquarePlus, X, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';

const CATEGORIES = [
  { v: 'SUGERENCIA', l: 'Sugerencia' },
  { v: 'PROBLEMA', l: 'Problema' },
  { v: 'OTRO', l: 'Otro' },
];
const MAX = 2000;

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('SUGERENCIA');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    // Reset diferido para no parpadear durante la animación de cierre.
    setTimeout(() => { setCategory('SUGERENCIA'); setMessage(''); setErr(null); setDone(false); setSubmitting(false); }, 150);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = message.trim();
    if (!msg) { setErr('Escribe un mensaje.'); return; }
    setSubmitting(true); setErr(null);
    try {
      await api('/feedback', { method: 'POST', body: { category, message: msg } });
      setDone(true);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo enviar. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title="Enviar comentario"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-2 text-xs font-medium text-slate-600 hover:bg-slate-50 sm:px-3 sm:py-2">
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Enviar comentario</span>
      </button>

      {/* Portal a document.body: el botón vive en el <header> del shell, que tiene
          `backdrop-blur`. Un ancestro con backdrop-filter crea un containing block,
          así que un `position:fixed` se ancla a ESE header (barra delgada arriba) en
          vez del viewport → el modal quedaba pegado y cortado arriba. El portal lo
          saca del header y lo ancla al viewport, como los demás modales. */}
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={close} />
          {/* max-h-[90dvh] + flex-col + cuerpo con overflow-y-auto: sin esto, si el
              contenido pasaba del alto de la ventana el modal se desbordaba (hacia
              arriba en el bottom-sheet móvil, cortado arriba/abajo en desktop) y el
              textarea quedaba fuera de alcance. dvh usa el viewport dinámico del móvil. */}
          <div className="relative flex max-h-[90dvh] w-full max-w-lg flex-col rounded-t-3xl border border-slate-200/80 bg-white shadow-xl md:rounded-3xl">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-6 pt-6 pb-4 md:px-8">
              <div>
                <h3 className="font-serif text-2xl tracking-tight text-slate-900">Enviar comentario</h3>
                <p className="text-sm text-slate-500">Tu sugerencia o problema llega directo al equipo.</p>
              </div>
              <button onClick={close} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>

            {done ? (
              <div className="flex flex-col items-center gap-3 overflow-y-auto px-6 py-10 text-center md:px-8">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                <p className="font-medium text-slate-800">¡Gracias! Recibimos tu comentario.</p>
                <button onClick={close} className="mt-2 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">Cerrar</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 overflow-y-auto px-6 py-6 md:px-8">
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Categoría</span>
                  <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Mensaje <span className="text-rose-700">*</span></span>
                  <textarea className="input min-h-[120px] resize-y" maxLength={MAX} value={message}
                    onChange={(e) => setMessage(e.target.value)} placeholder="Cuéntanos qué mejorarías o qué problema tuviste…" />
                  <span className="mt-1 block text-right text-[11px] text-slate-400">{message.length}/{MAX}</span>
                </label>
                {err && <p className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={close} className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">Cancelar</button>
                  <button type="submit" disabled={submitting || !message.trim()}
                    className="rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                    {submitting ? 'Enviando…' : 'Enviar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
